import React from "react";
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';
import { Link } from "@arctheme-components/elements/Link/Link";
import Paragraph from "@arctheme-components/elements/Paragraph/Paragraph";
import { Image } from "@project-stories/elements/Image/Image";

interface BlogBarProps {
  settings: {
    settings: {
      show_author?: boolean;
      show_date?: boolean;
      show_read_time?: boolean;
      show_social_sharing?: boolean;
      show_breadcrumbs?: boolean;
      mobile_breakpoint?: number;
    };
    article?: {
      author?: string;
      published_at?: string;
      content?: string;
      tags?: string[];
      url?: string;
      title?: string;
    };
    blog?: {
      title?: string;
      url?: string;
    };
  };
}

export const BlogBar: React.FC<BlogBarProps> = ({ settings }) => {
  const { 
    show_author = true, 
    show_date = true, 
    show_read_time = true,
    show_social_sharing = true,
    show_breadcrumbs = true 
  } = settings.settings.section || {};
  
  const isMobile = useMobileBreakpoint(settings.settings.mobile_breakpoint);
  const article = settings.settings.article || {};
  const blog = settings.settings.blog || {};

  const wordsPerMinute = 200;
  const text = article.content || "";
  const wordCount = text.split(/\s+/).length;
  article.readTime = Math.ceil(wordCount / wordsPerMinute);

  article.date = article.published_at;
  article.authorName = article.author;
  article.authorImage = 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR4g_2Qj3LsNR-iqUAFm6ut2EQVcaou4u2YXw&s';

  // Calculate read time (roughly 200 words per minute)
  const readTime = React.useMemo(() => {
    if (!article.content) return "< 1 min";
    const wordCount = article.content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  }, [article.content]);

  // Format date
  const formattedDate = React.useMemo(() => {
    if (!article.published_at) return "";
    const date = new Date(article.published_at);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }, [article.published_at]);

  // Current URL for sharing
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className={`container blog-bar mt-[112px] gap-8 ${isMobile ? 'flex-col' : ' flex-col flex'}`}>
    
      {/* User info, date and read time */}
      <div className={`user-info flex flex-col ${isMobile ? 'justify-between w-full' : ''}`}>
        <div className="flex items-center gap-4">
          {show_author && article.author && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                 <Image
                    imageUrl={typeof article.image === "object" && article.image.src ? article.image.src : article.image}
                    classes="blog-article-tile__image"
                />
              </div>
            </div>
          )}


          <div className="flex flex-col">
            {show_author && article.author && (
              <span className="text-sm font-medium">{article.author}</span>
            )}
            <div className="flex gap-4">
                {show_date && article.date && (
                    <div className="b_alt">{article.date}</div>
                )}

                {show_read_time && readTime && (
                    <div className="b_alt">{readTime}</div>
                )}
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      {show_breadcrumbs && (
        <div className={`flex max-lg:flex-col`}>
    
            <div className={`breadcrumbs mb-${isMobile ? '4' : '0'} ${isMobile ? 'w-full' : 'flex-1'}`}>
            <nav className="flex" aria-label="Breadcrumb">
                <ol className="inline-flex items-center space-x-1 md:space-x-3">
                <li className="inline-flex items-center">
                    <Link link="/" classes="b3">
                    Home
                    </Link>
                </li>
                {blog.title && (
                    <li>
                    <div className="flex items-center">
                        <span className="mx-2">/</span>
                        <Link link={blog.url || '#'} classes="b3">
                        {blog.title}
                        </Link>
                    </div>
                    </li>
                )}
                {article.title && (
                    <li aria-current="page">
                    <div className="flex items-center">
                        <span className="mx-2">/</span>
                        <span className="b3 mx-2">
                        {article.title}
                        </span>
                    </div>
                    </li>
                )}
                </ol>
            </nav>
            </div>

            <div className="social-sharing flex gap-2">
              <a 
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(article.title || '')}`} 
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Share on Twitter"
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="text-gray-600" viewBox="0 0 16 16">
                  <path d="M5.026 15c6.038 0 9.341-5.003 9.341-9.334 0-.14 0-.282-.006-.422A6.685 6.685 0 0 0 16 3.542a6.658 6.658 0 0 1-1.889.518 3.301 3.301 0 0 0 1.447-1.817 6.533 6.533 0 0 1-2.087.793A3.286 3.286 0 0 0 7.875 6.03a9.325 9.325 0 0 1-6.767-3.429 3.289 3.289 0 0 0 1.018 4.382A3.323 3.323 0 0 1 .64 6.575v.045a3.288 3.288 0 0 0 2.632 3.218 3.203 3.203 0 0 1-.865.115 3.23 3.23 0 0 1-.614-.057 3.283 3.283 0 0 0 3.067 2.277A6.588 6.588 0 0 1 .78 13.58a6.32 6.32 0 0 1-.78-.045A9.344 9.344 0 0 0 5.026 15z"/>
                </svg>
              </a>
              <a 
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`}
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Share on Facebook"
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="text-gray-600" viewBox="0 0 16 16">
                  <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z"/>
                </svg>
              </a>
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`}
                target="_blank" 
                rel="noopener noreferrer"
                aria-label="Share on LinkedIn"
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="text-gray-600" viewBox="0 0 16 16">
                  <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z"/>
                </svg>
              </a>
            </div>
        </div>
      )}
    </div>
  );
};

export default BlogBar;
